/**
 * StandaloneChat — Floating chat overlay for in-game messaging
 *
 * Small overlay (not a UIManager modal) that uses the same ChatPanel.
 * Created on demand when the notification bubble is tapped during a game.
 * Includes a conversation sidebar so players can switch between chats.
 * z-index: 600 (below notification bubble at 700)
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { ChatPanel } from './ChatPanel';
import type { ChatService } from '../../../services/ChatService';
import type { Conversation, StoredMessage } from '../../../db/ChatDatabase';

// ─── Constants ────────────────────────────────────────────────────────

const STYLE_ID = 'standalone-chat-styles';

// ─── Types ────────────────────────────────────────────────────────────

interface StandaloneChatEvents {
  'close': void;
  /** Header avatar/name tapped — open the active peer's player card. */
  'profile:tap': { playerId: string; username?: string; picture?: string };
}

export interface StandaloneChatOptions {
  container: HTMLElement;
  chatService: ChatService;
  localPlayerId: string;
  /**
   * Conversation to open on mount. Omit (chat-bubble path always passes one,
   * the in-game menu does not) to open on the most-recent conversation, or an
   * empty "no conversations yet" state when the player has none.
   */
  peerId?: string;
  peerUsername?: string;
  peerPicture?: string;
  /** Resolve a peer's freshest identity from the crew list (avatar/name). */
  resolvePeer?: (id: string) => { username?: string; picture?: string } | undefined;
}

// ─── Component ────────────────────────────────────────────────────────

export class StandaloneChat extends EventEmitter<StandaloneChatEvents> {
  public element: HTMLElement;
  private chatPanel: ChatPanel | null = null;
  private chatService: ChatService;
  private localPlayerId: string;
  private activePeerId: string;
  private activePeerUsername: string;
  private activePeerPicture: string;
  private resolvePeer?: (id: string) => { username?: string; picture?: string } | undefined;
  /** Identity passed by the opener — only valid for the initially-opened peer. */
  private optionPeerId: string;
  private optionUsername: string;
  private optionPicture: string;
  private container: HTMLElement;

  // DOM refs
  private sidebarEl: HTMLElement;
  private headerAvatarEl: HTMLElement;
  private headerNameEl: HTMLElement;
  private bodyEl: HTMLElement;

  // Conversations
  private conversations: Conversation[] = [];

  // Bound handlers for cleanup
  private messageHandler: ((msg: StoredMessage) => void) | null = null;
  private conversationsHandler: ((convos: Conversation[]) => void) | null = null;

  constructor(options: StandaloneChatOptions) {
    super();
    this.container = options.container;
    this.chatService = options.chatService;
    this.localPlayerId = options.localPlayerId;
    this.activePeerId = options.peerId ?? '';
    this.activePeerUsername = options.peerUsername ?? 'Player';
    this.activePeerPicture = options.peerPicture ?? '';
    this.resolvePeer = options.resolvePeer;
    this.optionPeerId = options.peerId ?? '';
    this.optionUsername = options.peerUsername ?? '';
    this.optionPicture = options.peerPicture ?? '';

    this.injectStyles();

    // Build DOM
    this.element = this.createElement();
    this.sidebarEl = this.element.querySelector('.standalone-chat-sidebar')!;
    this.headerAvatarEl = this.element.querySelector('.standalone-chat-avatar')!;
    this.headerNameEl = this.element.querySelector('.standalone-chat-name')!;
    this.bodyEl = this.element.querySelector('.standalone-chat-body')!;

    // Mount
    this.container.appendChild(this.element);

    // Load sidebar conversations then open initial chat
    this.loadConversationsAndOpen(options.peerId);

    // Setup live listeners
    this.setupServiceListeners();

    // Entrance animation
    requestAnimationFrame(() => {
      this.element.classList.add('visible');
    });
  }

  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'standalone-chat';

    el.innerHTML = `
      <div class="standalone-chat-sidebar"></div>
      <div class="standalone-chat-main">
        <div class="standalone-chat-header">
          <div class="standalone-chat-avatar"></div>
          <div class="standalone-chat-name"></div>
          <button class="standalone-chat-close">&times;</button>
        </div>
        <div class="standalone-chat-body"></div>
      </div>
    `;

    el.querySelector('.standalone-chat-close')!.addEventListener('click', () => {
      this.close();
    });

    // Tap the header avatar/name → open the active peer's player card.
    el.querySelector('.standalone-chat-header')!.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('.standalone-chat-close')) return;
      if (!t.closest('.standalone-chat-avatar') && !t.closest('.standalone-chat-name')) return;
      if (!this.activePeerId) return;
      this.emit('profile:tap', {
        playerId: this.activePeerId,
        username: this.activePeerUsername,
        picture: this.activePeerPicture,
      });
    });

    return el;
  }

  private async loadConversationsAndOpen(peerId?: string): Promise<void> {
    this.conversations = await this.chatService.getConversations();
    this.renderSidebar();
    // Menu-launched (no peer): fall back to the most-recent conversation, or an
    // empty state when the player has never chatted. Bubble-launched always
    // passes the sender, so it opens straight into that thread.
    const target = peerId || this.conversations[0]?.peerId;
    if (target) {
      await this.openConversation(target);
    } else {
      this.renderEmptyState();
    }
  }

  /** Shown when opened from the menu with zero existing conversations. */
  private renderEmptyState(): void {
    this.activePeerId = '';
    this.headerAvatarEl.style.backgroundImage = '';
    this.headerAvatarEl.textContent = '';
    this.headerNameEl.textContent = 'Chat';
    if (this.chatPanel) {
      this.chatPanel.destroy();
      this.chatPanel = null;
    }
    this.bodyEl.innerHTML = `<div class="standalone-chat-empty">No conversations yet.<br>Start one from a player's card.</div>`;
  }

  private async openConversation(peerId: string): Promise<void> {
    this.activePeerId = peerId;

    // Find conversation info. Prefer the crew list (freshest avatar/name),
    // then any caller-provided identity, then stored conversation metadata.
    const convo = this.conversations.find(c => c.peerId === peerId);
    const resolved = this.resolvePeer?.(peerId);
    // Opener-provided identity only applies to the peer it was opened for.
    const optUser = peerId === this.optionPeerId ? this.optionUsername : '';
    const optPic = peerId === this.optionPeerId ? this.optionPicture : '';
    this.activePeerUsername = resolved?.username || convo?.peerUsername || optUser || 'Player';
    this.activePeerPicture = resolved?.picture || convo?.peerPicture || optPic || '';

    // Update header
    if (this.activePeerPicture) {
      this.headerAvatarEl.style.backgroundImage = `url(${this.activePeerPicture})`;
      this.headerAvatarEl.textContent = '';
    } else {
      this.headerAvatarEl.style.backgroundImage = '';
      this.headerAvatarEl.textContent = this.activePeerUsername.charAt(0).toUpperCase();
    }
    this.headerNameEl.textContent = this.activePeerUsername;

    // Destroy existing panel
    if (this.chatPanel) {
      this.chatPanel.destroy();
      this.chatPanel = null;
    }

    // Create new panel
    this.chatPanel = new ChatPanel({
      localPlayerId: this.localPlayerId,
      peerId,
      peerUsername: this.activePeerUsername,
      peerPicture: this.activePeerPicture,
    });

    // Wire send
    this.chatPanel.on('send', ({ content, type }) => {
      this.chatService.sendMessage(peerId, this.activePeerUsername, this.activePeerPicture, content, type);
    });

    this.bodyEl.innerHTML = '';
    this.bodyEl.appendChild(this.chatPanel.element);

    // Load messages
    const messages = await this.chatService.getMessages(peerId, 50);
    this.chatPanel.setMessages(messages);

    // Mark as read
    this.chatService.markRead(peerId);

    // Update sidebar active state
    this.updateSidebarActive();
  }

  private renderSidebar(): void {
    this.sidebarEl.innerHTML = '';

    for (const convo of this.conversations) {
      const avatar = document.createElement('div');
      avatar.className = `chat-sidebar-avatar ${convo.peerId === this.activePeerId ? 'active' : ''}`;
      avatar.dataset.peerId = convo.peerId;

      const resolved = this.resolvePeer?.(convo.peerId);
      const picture = resolved?.picture || convo.peerPicture;
      const initial = (resolved?.username || convo.peerUsername).charAt(0).toUpperCase();
      if (picture) {
        avatar.innerHTML = `<img src="${picture}" alt="${initial}">`;
      } else {
        avatar.innerHTML = `<span>${initial}</span>`;
      }

      // Unread badge
      if (convo.unreadCount > 0) {
        const badge = document.createElement('span');
        badge.className = 'chat-sidebar-badge';
        badge.textContent = convo.unreadCount > 99 ? '99+' : String(convo.unreadCount);
        avatar.appendChild(badge);
      }

      avatar.addEventListener('click', () => {
        this.openConversation(convo.peerId);
      });

      this.sidebarEl.appendChild(avatar);
    }
  }

  private updateSidebarActive(): void {
    this.sidebarEl.querySelectorAll('.chat-sidebar-avatar').forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.classList.toggle('active', htmlEl.dataset.peerId === this.activePeerId);
    });
  }

  private setupServiceListeners(): void {
    // Live incoming messages
    this.messageHandler = (msg: StoredMessage) => {
      if (this.chatPanel && (msg.peerId === this.activePeerId || msg.from === this.activePeerId)) {
        this.chatPanel.appendMessage(msg);
        if (msg.from !== this.localPlayerId) {
          this.chatService.markRead(this.activePeerId);
        }
      }
    };
    this.chatService.on('message:received', this.messageHandler as any);

    // Conversation list updates (new messages, read status changes)
    this.conversationsHandler = (convos: Conversation[]) => {
      this.conversations = convos;
      this.renderSidebar();
    };
    this.chatService.on('conversations:updated', this.conversationsHandler as any);
  }

  close(): void {
    this.element.classList.remove('visible');
    setTimeout(() => {
      // Emit BEFORE destroy: destroy() calls removeAllListeners(), so emitting
      // after it would drop the 'close' handler and never null out the caller's
      // reference — leaving the chat "open" forever and suppressing the bubble.
      this.emit('close', undefined);
      this.destroy();
    }, 300);
  }

  destroy(): void {
    if (this.chatPanel) {
      this.chatPanel.destroy();
      this.chatPanel = null;
    }
    if (this.messageHandler) {
      this.chatService.off('message:received', this.messageHandler as any);
    }
    if (this.conversationsHandler) {
      this.chatService.off('conversations:updated', this.conversationsHandler as any);
    }
    this.element.remove();
    this.removeAllListeners();
  }

  // ─── Styles ─────────────────────────────────────────────────────────

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .standalone-chat {
        position: absolute;
        right: 12px;
        bottom: 80px;
        width: 370px;
        max-width: calc(100vw - 24px);
        height: 420px;
        max-height: 50vh;
        z-index: 600;
        background: rgba(8, 8, 10, 0.95);
        border: 1px solid rgba(255, 0, 128, 0.2);
        border-radius: 16px;
        overflow: hidden;
        display: flex;
        flex-direction: row;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 12px rgba(255, 0, 128, 0.15);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);

        opacity: 0;
        transform: translateY(20px) scale(0.95);
        transition: opacity 0.3s ease, transform 0.3s ease;
      }

      .standalone-chat.visible {
        opacity: 1;
        transform: translateY(0) scale(1);
        pointer-events: auto;
      }

      /* ─── Sidebar ─── */
      .standalone-chat-sidebar {
        width: 52px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 8px 0;
        gap: 8px;
        overflow-y: auto;
        border-right: 1px solid rgba(255, 255, 255, 0.06);
      }

      /* Reuse .chat-sidebar-avatar / .chat-sidebar-badge from CrewScreen styles */

      /* ─── Main area ─── */
      .standalone-chat-main {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        min-width: 0;
      }

      .standalone-chat-header {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        flex-shrink: 0;
      }

      .standalone-chat-avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: #242429;
        background-size: cover;
        background-position: center;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 14px;
        color: #FF0080;
        flex-shrink: 0;
        border: 1.5px solid rgba(255, 0, 128, 0.3);
      }

      .standalone-chat-avatar { cursor: pointer; }
      .standalone-chat-name {
        flex: 1;
        cursor: pointer;
        font-family: 'Rajdhani', sans-serif;
        font-weight: 600;
        font-size: 14px;
        color: #fff;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .standalone-chat-close {
        width: 28px;
        height: 28px;
        border: none;
        background: rgba(255, 255, 255, 0.06);
        color: rgba(255, 255, 255, 0.5);
        border-radius: 50%;
        font-size: 18px;
        line-height: 1;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: background 0.15s ease, color 0.15s ease;
      }

      .standalone-chat-close:hover {
        background: rgba(255, 51, 102, 0.15);
        color: #FF3366;
      }

      .standalone-chat-body {
        flex: 1;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      .standalone-chat-body .chat-panel {
        flex: 1;
        height: 100%;
      }

      .standalone-chat-empty {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 20px;
        font-family: 'Rajdhani', sans-serif;
        font-weight: 500;
        font-size: 14px;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.4);
      }
    `;
    document.head.appendChild(style);
  }
}
